/**
 * The dark banner shared by the two management planes (round9/design-pass).
 *
 * Extracted from pages/admin/AdminLayout.tsx so the org-admin section can
 * carry the same chrome: before this, /organization/* rendered NO header,
 * no nav and no Sign out at all — an org admin's only way out of the portal
 * was clearing browser storage. The two planes differ by tone (platform
 * admin is --blue-deep, an organization is --blue-dark) and by the tag under
 * the wordmark, so an operator is never in doubt which surface they are on,
 * and neither is mistaken for the builder portal's light chrome.
 *
 * Purely presentational: identity, nav items and the sign-out handler come
 * from the caller (AdminLayout reads the admin session; OrgShell reads the
 * tenant-plane session), so this component knows nothing about auth.
 */
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import styles from "./PlaneBanner.module.css";

export interface PlaneNavItem {
  to: string;
  label: string;
  /** NavLink `end` — match this path exactly, not as a prefix. */
  end?: boolean;
}

export function PlaneBanner({
  tone,
  tag,
  navLabel,
  items,
  identity,
  onSignOut,
}: {
  tone: "deep" | "dark";
  /** The line under "DockPro": "Platform admin", or the organization's name. */
  tag: string;
  navLabel: string;
  items: readonly PlaneNavItem[];
  identity?: ReactNode;
  onSignOut: () => void;
}) {
  return (
    <header className={tone === "deep" ? styles.bannerDeep : styles.bannerDark}>
      <div className={styles.inner}>
        <div className={styles.brand}>
          <span className={styles.brandMark}>DockPro</span>
          <span className={styles.brandTag}>{tag}</span>
        </div>
        <nav aria-label={navLabel} className={styles.nav}>
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => (isActive ? styles.navLinkActive : styles.navLink)}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className={styles.identity}>
          {identity}
          <button type="button" onClick={onSignOut} className={styles.signOutButton}>
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}
